<template>
  <v-card flat class="pa-2">
    <div :style="`height: ${mapHeight - computedOffsetTop}px`" class="mb-2">
      <LMap
        :zoom="zoom"
        :bounds="mapBounds"
        :center="center"
        :options="{ zoomControl: false, attributionControl: false }"
        style="height: 100%; width: 100%; z-index: 0"
      >
        <LTileLayer
          v-for="tile in tileProviders"
          :key="tile.name"
          :name="tile.name"
          :visible="tile.visible"
          :url="tile.url"
          :attribution="tile.attribution"
          layer-type="base"
        />
        <LControlZoom position="topright" />
        <LControlLayers position="topright" />
        <LControl position="topright">
          <v-menu open-on-hover :close-on-content-click="false">
            <template #activator="{ on }">
              <v-card v-on="on" flat><v-icon large class="ma-1">settings</v-icon></v-card>
            </template>
            <template #default>
              <v-list>
                <v-switch
                  v-model="clusterElements"
                  dense
                  label="Cluster elements"
                  class="mx-1"
                ></v-switch>
              </v-list>
            </template>
          </v-menu>
        </LControl>
        <LControl position="topleft">
          <v-card>
            <v-card-text>{{ this.caseName }}</v-card-text>
          </v-card>
        </LControl>
        <LControl position="bottomright" class="logo-control">
          <img src="@/assets/rex-logo.png" alt="Acelerex" class="logo" />
        </LControl>
        <LControl v-if="showHeatmap" position="bottomright" class="heatmap-control">
          <v-card width="128" outlined elevation="0">
            <v-row class="no-gutters">
              <v-card class="grow" flat>
                <p class="my-0 text-caption font-weight-medium text-center">
                  {{ camelToTitleCase(heatmapMeasure) }}
                </p>
                <p v-if="heatmapUnit" class="my-0 pl-1 text-caption font-weight-medium text-center">
                  {{ heatmapUnit }}
                </p>
              </v-card>
            </v-row>
            <v-row v-for="color in heatmapScale" :key="color.code" class="no-gutters">
              <v-card class="d-flex grow pa-1" flat>
                <p class="mb-0 ml-1">{{ color.range }}</p>
                <v-spacer />
                <v-card :color="color.code" height="16" width="16" flat></v-card>
              </v-card>
            </v-row>
          </v-card>
        </LControl>
        <LLayerGroup name="Nodes" layer-type="overlay">
          <template v-if="nodes.length">
            <LMarker
              v-for="node in nodes"
              :key="node.id"
              :lat-lng="node.coordinates"
              :icon="nodeIcon"
              ><LPopup>
                <div>
                  {{ `${node.id} - ${node.name}` }}
                </div>
              </LPopup>
            </LMarker>
          </template>
        </LLayerGroup>
        <LLayerGroup name="Branches" layer-type="overlay">
          <template v-if="branches">
            <LPolyline
              v-for="(branch, i) in branches"
              :key="i"
              :lat-lngs="branchPoints(branch)"
              color="black"
            />
          </template>
        </LLayerGroup>
        <LLayerGroup name="Projects" layer-type="overlay">
          <template v-if="projects.length">
            <VMarkerCluster
              :options="clusterOptions"
              ref="clusterRef"
              :key="`cluster-${clusterKey}`"
            >
              <LMarker
                v-for="(project, i) in projects"
                :key="i"
                :lat-lng="project.coordinates"
                :icon="getIcon(project.iconType, project.color)"
                @update:latLng="projectUpdate($event, project)"
              >
                <LPopup :options="{ offset: [0, -24] }">
                  <div>
                    <v-card flat width="240">
                      <p class="text-subtitle-1 my-1">
                        <router-link
                          :to="{
                            path: `/case/${caseId}/input#Project`,
                            query: { projectName: project.name },
                          }"
                        >
                          {{ `${i} ` }} {{ project.name }}
                        </router-link>
                      </p>
                      <p v-if="showHeatmap" class="my-0">
                        {{ outputValue(project.name) }}
                      </p>
                    </v-card>
                  </div>
                </LPopup>
              </LMarker>
            </VMarkerCluster>
          </template>
        </LLayerGroup>
        <LLayerGroup name="Opportunity Zones" layer-type="overlay">
          <LPolygon
            v-for="zone in computedOpportunityZones"
            :key="zone.id"
            :lat-lngs="zone.latLng"
            :stroke="false"
            fill-color="#E65100"
            :fill-opacity="0.5"
          ></LPolygon>
        </LLayerGroup>
        <LLayerGroup name="Heatmap" layer-type="overlay" v-if="showHeatmap">
          <LInterpolation
            :lat-lng="heatMapLatLng"
            :bounds="heatmapBounds"
            ref="idwComponent"
            :cellSize="heatmapCellSize"
            discrete
          >
          </LInterpolation>
        </LLayerGroup>
        <LLayerGroup
          v-for="layer in mapLayers"
          :key="layer.id"
          :name="layer.name"
          layer-type="overlay"
          :visible="false"
        >
          <template v-for="feature in layerFeatures(layer)">
            <LCircleMarker
              v-if="feature.geomType === 'point'"
              :key="feature.featureId"
              :lat-lng="[feature.lat, feature.lon]"
              :radius="layerRadius(layer, feature.value)"
              :color="strokeColor(layer, feature)"
              :fill-color="fillColor(layer, feature)"
              :fill-opacity="0.85"
              :weight="2"
              :dash-array="feature.style === 'dashed' ? '3,3' : null"
            >
              <LPopup :options="{ offset: [0, -4] }">
                <v-card flat width="220">
                  <p class="text-subtitle-2 mb-1">{{ feature.name }}</p>
                  <p v-if="feature.owner" class="my-0 text-caption">{{ feature.owner }}</p>
                  <p v-if="feature.location" class="my-0 text-caption">{{ feature.location }}</p>
                  <p v-if="feature.status" class="my-0 text-caption">{{ feature.status }}</p>
                  <p v-if="feature.value != null" class="my-0 text-caption">
                    {{ feature.valueLabel || (layer.valueDomain && layer.valueDomain.label) }}:
                    {{ feature.value }}
                  </p>
                  <p v-if="feature.date" class="my-0 text-caption">{{ feature.date }}</p>
                  <p v-if="feature.notes" class="my-0 text-caption">{{ feature.notes }}</p>
                </v-card>
              </LPopup>
            </LCircleMarker>
            <LPolyline
              v-else
              :key="feature.featureId"
              :lat-lngs="feature.points"
              :color="fillColor(layer, feature)"
              :weight="2.4"
              :dash-array="feature.style === 'dashed' ? '8,6' : null"
            >
              <LPopup>
                <v-card flat width="220">
                  <p class="text-subtitle-2 mb-1">{{ feature.name }}</p>
                  <p v-if="feature.owner" class="my-0 text-caption">{{ feature.owner }}</p>
                  <p v-if="feature.status" class="my-0 text-caption">{{ feature.status }}</p>
                  <p v-if="feature.value != null" class="my-0 text-caption">
                    {{ feature.valueLabel || (layer.valueDomain && layer.valueDomain.label) }}:
                    {{ feature.value }}
                  </p>
                  <p v-if="feature.notes" class="my-0 text-caption">{{ feature.notes }}</p>
                </v-card>
              </LPopup>
            </LPolyline>
          </template>
        </LLayerGroup>
        <LControl v-if="mapLayers.length" position="bottomleft">
          <MapLayerLegendPanel v-model="mapLayerFilters" :layers="mapLayers" />
        </LControl>
      </LMap>
    </div>
    <v-toolbar v-if="hasOutputs">
      <template>
        <v-col sm="2" cols="2" class="mt-4">
          <v-checkbox
            v-model="showHeatmap"
            label="Heatmap"
            dense
            :disabled="!hasOutputs"
          ></v-checkbox>
        </v-col>
        <v-spacer />
        <v-col sm="2" cols="2" class="mt-5">
          <v-select
            v-model="heatmapYear"
            label="Year"
            :items="outputYears"
            dense
            menu-props="top offset-y"
          />
        </v-col>
        <v-col sm="2" cols="2" class="mt-5">
          <v-select
            v-model="heatmapAsset"
            label="Asset"
            :items="availableAssetsItems"
            dense
            menu-props="top offset-y"
          />
        </v-col>
        <v-col sm="3" cols="2" class="mt-5">
          <v-select
            v-model="heatmapMeasure"
            label="Measure"
            :items="measures"
            dense
            menu-props="top offset-y"
          />
        </v-col>
        <v-spacer />
        <v-menu v-if="showHeatmap" top left offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on: menuOn }">
            <v-tooltip top>
              <template #activator="{ on: tooltipOn }">
                <v-btn icon v-on="{ ...tooltipOn, ...menuOn }" medium>
                  <v-icon color="primary">settings</v-icon>
                </v-btn>
              </template>
              <span>Settings</span>
            </v-tooltip>
          </template>
          <v-card width="200">
            <v-card-text>
              <span class="mb-2 text-subtitle-2">Scale Limits</span>
              <v-radio-group v-model="scaleOption" class="my-0" dense>
                <v-radio value="auto" label="Auto"></v-radio>
                <v-radio value="custom" label="Custom"></v-radio>
              </v-radio-group>
              <v-text-field
                v-model.number="scaleCustomMax"
                label="Max."
                type="number"
                :minumum="scaleCustomMin + 1"
                :disabled="scaleOption === 'auto'"
                dense
              />
              <v-text-field
                v-model.number="scaleCustomMin"
                label="Min."
                type="number"
                :maximum="scaleCustomMax - 2"
                :disabled="scaleOption === 'auto'"
                dense
              />
              <span class="mb-2 text-subtitle-2">Resolution</span>
              <v-text-field
                v-model.number="heatmapCellSize"
                label=""
                type="number"
                :min="5"
                dense
              />
            </v-card-text>
          </v-card>
        </v-menu>
      </template>
    </v-toolbar>
    <v-toolbar class="mt-2">
      <v-col sm="2" class="mt-4">
        <p>
          Opportunity Zones
          <v-progress-circular
            v-if="isFindOpportunityZonesPending || isBatchPending"
            indeterminate
            color="secondary"
            size="24"
            class="ml-2"
          ></v-progress-circular>
        </p>
      </v-col>
      <v-col class="mt-4">
        <v-select
          v-model="selectedStates"
          label="States"
          :items="states"
          item-text="stateName"
          multiple
          class="ml-8"
          return-object
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 5" small>
              <span>{{ item.stateName }}</span>
            </v-chip>
            <span v-if="index == 5" class="grey--text text-caption">
              (+{{ selectedStates.length - 5 }} others)
            </span>
          </template>
        </v-select>
      </v-col>
    </v-toolbar>
    <v-expansion-panels class="mt-2" tile>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <p class="mb-0">
            Custom Map Layers
            <span class="grey--text text-caption ml-2">({{ mapLayers.length }})</span>
          </p>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-list dense>
            <v-list-item v-if="!mapLayers.length">
              <v-list-item-title class="text-caption grey--text">No layers added</v-list-item-title>
            </v-list-item>
            <v-list-item v-for="layer in mapLayers" :key="layer.id">
              <v-list-item-content>
                <v-text-field
                  v-if="renamingLayerId === layer.id"
                  v-model="renameValue"
                  dense
                  hide-details
                  autofocus
                  @keyup.enter="confirmRename(layer)"
                  @blur="confirmRename(layer)"
                ></v-text-field>
                <v-list-item-title v-else>{{ layer.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="startRename(layer)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </v-list-item-action>
              <v-list-item-action>
                <v-btn icon small @click="removeMapLayer(layer.id)">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="mapLayers.length"></v-divider>
            <v-list-item>
              <MapLayerUpload @add-layer="addMapLayer" />
            </v-list-item>
          </v-list>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script>
import states from "@/assets/states.json";
import { camelToTitleCase } from "@/plugins/utils";
import { makeFindMixin } from "feathers-vuex";
import { divIcon, latLngBounds } from "leaflet";
import {
  LCircleMarker,
  LControl,
  LControlLayers,
  LControlZoom,
  LLayerGroup,
  LMap,
  LMarker,
  LPolygon,
  LPolyline,
  LPopup,
  LTileLayer,
} from "vue2-leaflet";
import Vue2LeafletMarkercluster from "vue2-leaflet-markercluster";

export default {
  name: "VisGeo",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPolyline,
    LCircleMarker,
    LPopup,
    LControl,
    LControlLayers,
    LControlZoom,
    LLayerGroup,
    LPolygon,
    VMarkerCluster: Vue2LeafletMarkercluster,
    LInterpolation: () => import("@/components/LInterpolation.vue"),
    MapLayerLegendPanel: () => import("@/components/MapLayerLegendPanel.vue"),
    MapLayerUpload: () => import("@/components/MapLayerUpload.vue"),
  },
  mixins: [
    makeFindMixin({
      service: "opportunity-zones",
    }),
  ],
  props: {
    startYear: { type: [Number, String], required: true },
    caseId: { type: [Number, String], required: false, default: "" },
    caseName: { type: String, required: false, default: null },
    nodes: { type: Array, required: false, default: () => [] },
    branches: { type: Array, required: false, default: () => [] },
    projects: { type: Array, required: false, default: () => [] },
    outputs: { type: Object, required: false, default: null },
    mapLayers: { type: Array, required: false, default: () => [] },
    height: { type: String, required: false, default: "500" },
    bounds: { type: [Array, Object] },
    offsetTop: { type: [Boolean, Number], required: false, default: false },
  },
  data() {
    return {
      zoom: 9,
      tileProviders: [
        {
          name: "Map",
          visible: true,
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        },
        {
          name: "Satellite",
          visible: false,
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          attribution:
            "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
        },
      ],
      center: [40.71, -74.006],
      clusterKey: 1,
      showHeatmap: false,
      heatmapVisible: true,
      heatmapYear: this.startYear.toString(),
      heatmapAsset: "storages",
      heatmapMeasure: "sales",
      measures: [
        { text: "Annual Purchases", value: "purchases" },
        { text: "Annual Sales", value: "sales" },
        { text: "Annual Cycles", value: "cycles" },
        { text: "Annual Energy Throughput", value: "energyThroughput" },
        { text: "Annual P&L", value: "pL" },
        { text: "Monthly Purchases", value: "monthlyPurchases" },
        { text: "Monthly Sales", value: "monthlySales" },
        { text: "Monthly P&L", value: "monthlyPL" },
      ],

      heatmapCellSize: 20,
      scaleOption: "auto",
      scaleCustomMin: 0,
      scaleCustomMax: 0,
      clusterElements: true,

      states,
      selectedStates: [],
      isBatchPending: false,
      mapLayerFilters: {},
      renamingLayerId: null,
      renameValue: "",
    };
  },
  computed: {
    computedOffsetTop() {
      return this.offsetTop ? 115 : 68;
    },
    hasOutputs() {
      return Boolean(Object.keys(this.outputs).length);
    },
    windowHeight() {
      return this.$vuetify.breakpoint.height;
    },
    mapHeight() {
      return (this.windowHeight - 130) * 0.99;
    },
    mapBounds() {
      if (!this.projects.length && !this.nodes.length) {
        return null;
      }
      const bounds = latLngBounds([
        ...this.projects.map((p) => p.coordinates),
        ...this.nodes.map((n) => n.coordinates),
      ]);
      return Object.keys(bounds).length ? bounds.pad(0.3) : bounds;
    },
    outputYears() {
      return this.outputs ? Object.keys(this.outputs) : [];
    },
    availableAssetsItems() {
      const yearData = this.outputs && this.outputs[this.heatmapYear];
      const firstWithResults = yearData && yearData.find((p) => p.results);
      return firstWithResults
        ? Object.entries(firstWithResults.results)
            .map(([key, value]) => ({
              text: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
              value: key,
              disabled: !Object.keys(value).length,
            }))
            .sort((a, b) => {
              if (a.disabled === b.disabled) {
                return 0;
              }
              return a.disabled ? 1 : -1;
            })
        : [];
    },
    heatmapMax() {
      return this.scaleOption === "auto" ? this.maxValue : this.scaleCustomMax;
    },
    heatmapMin() {
      return this.scaleOption === "auto" ? this.minValue : this.scaleCustomMin;
    },
    maxValue() {
      return this.outputs && this.outputs[this.heatmapYear]
        ? Math.max(
            ...this.outputs[this.heatmapYear].map((project) =>
              project.results &&
              project.results[this.heatmapAsset] &&
              Object.values(project.results[this.heatmapAsset])[0]
                ? Object.values(project.results[this.heatmapAsset])[0][this.heatmapMeasure]
                : 0
            )
          )
        : 0;
    },
    minValue() {
      const minValue =
        this.outputs && this.outputs[this.heatmapYear]
          ? Math.min(
              ...this.outputs[this.heatmapYear].map((project) =>
                project.results &&
                project.results[this.heatmapAsset] &&
                Object.values(project.results[this.heatmapAsset])[0]
                  ? Object.values(project.results[this.heatmapAsset])[0][this.heatmapMeasure]
                  : 0
              )
            )
          : 0;
      return minValue === this.maxValue ? 0 : minValue;
    },
    // this.heatmapMax is here to update heatMapLatLng when heatmapMax changes.
    heatMapLatLng() {
      return this.showHeatmap && this.hasOutputs && this.heatmapMax
        ? this.projects.map((p) => {
            const found =
              this.outputs[this.heatmapYear] &&
              this.outputs[this.heatmapYear].find((project) => project.name === p.name);
            const value =
              found &&
              found.results &&
              found.results[this.heatmapAsset] &&
              Object.values(found.results[this.heatmapAsset])[0]
                ? (Object.values(found.results[this.heatmapAsset])[0][this.heatmapMeasure] -
                    this.heatmapMin) /
                  this.interpolationMax
                : 0;
            return [...p.coordinates, value];
          })
        : [];
    },
    // mapTitle() {
    //   return this.caseName;
    // },
    interpolationMax() {
      return this.heatmapMax - this.heatmapMin;
    },
    heatmapUnit() {
      if (["monthlyPL", "monthlyPurchases", "monthlySales"].includes(this.heatmapMeasure)) {
        return "[$/kW-month]";
      }
      if (["purchases", "sales", "pL"].includes(this.heatmapMeasure)) {
        return "[$/year]";
      }
      if (this.heatmapMeasure === "energyThroughput") return "[MWh]";
      return "";
    },
    heatmapScale() {
      const step = (this.heatmapMax - this.heatmapMin) / 8;
      const heatmapGradient = [
        "#4575b4",
        "#74add1",
        "#abd9e9",
        "#e0f3f8",
        "#fee090",
        "#fdae61",
        "#f46d43",
        "#d73027",
      ];
      const reversed = heatmapGradient
        .map((v, i) => ({
          range: `${this.formatValue(this.heatmapMin + (step * i + 1))} - ${this.formatValue(
            this.heatmapMin + step * (i + 1)
          )}`,
          code: v,
        }))
        .reverse();
      return reversed;
    },
    heatmapBounds() {
      return this.showHeatmap ? latLngBounds([...this.projects.map((p) => p.coordinates)]) : null;
    },
    opportunityZonesParams() {
      return { query: { stusab: { $in: this.selectedStates.map((s) => s.stusab) } } };
    },
    opportunityZonesByState() {
      return this.opportunityZones.reduce((obj, zone) => {
        const state = obj[zone.stusab] || [];
        state.push({ id: zone.objectId, latLng: zone.polygon });
        return { ...obj, [zone.stusab]: state };
      }, {});
    },
    computedOpportunityZones() {
      return this.selectedStates
        .map((s) => s.stusab)
        .flatMap((state) =>
          this.opportunityZonesByState[state] ? this.opportunityZonesByState[state] : []
        );
    },
    projectIcon() {
      return divIcon({
        className: "my-custom-pin",
        html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#306d8a" stroke="#90d7ec"/>
                <g transform="scale(0.7 0.7) translate(4.5 2.5)">
                <path d="M11.48 20v-6.26H8L13 4v6.26h3.35L11.48 20z" fill="white"/>
                </g>
              </svg>`,
        iconAnchor: [24, 44],
      });
    },
    nodeIcon() {
      return divIcon({
        className: "my-custom-pin",
        html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px" preserveAspectRatio="xMaxYMin meet">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#306d8a" stroke="#90d7ec"/>
              </svg>`,
        iconAnchor: [24, 44],
      });
    },
    clusterOptions() {
      return {
        maxClusterRadius: this.clusterElements ? 25 : 0,
        iconCreateFunction: (cluster) =>
          divIcon({
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px" preserveAspectRatio="xMaxYMin" text-rendering:geometricPrecision">
                <circle cx="12" cy="12" r="8" fill="#306d8a" stroke="#90d7ec" stroke-width="1.5"/>
                <g transform="translate(0 3)">
                <text x="50%" y="50%" text-anchor="middle" fill="#fff" font-size="x-small">${cluster.getChildCount()}</text>
              </svg>`,
            iconAnchor: [24, 20],
            className: "clusterIcon",
          }),
      };
    },
  },
  watch: {
    outputs: {
      immediate: true,
      handler(val) {
        if (val && Object.keys(val).length) {
          const firstYear = Object.keys(val).map(Number).sort()[0];
          this.heatmapYear = firstYear.toString();
        }
      },
    },
    heatmapMeasure() {
      this.scaleOption = "auto";
    },
    heatmapCellSize(v) {
      this.$refs.idwComponent.setCellSize(v);
    },
    heatMapLatLng(v) {
      if (this.$refs.idwComponent) {
        this.$refs.idwComponent.setLatLngs(v);
      }
    },
    clusterElements() {
      this.clusterKey += 1;
    },
    selectedStates() {
      this.getOpportunityZones();
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    camelToTitleCase,
    branchPoints(branch) {
      const fromNode = this.nodes.find((n) => n.id === branch.node_from);
      const toNode = this.nodes.find((n) => n.id === branch.node_to);
      const branchCoordinates = branch.coordinates ? branch.coordinates : [];
      const coordinates = [
        fromNode ? fromNode.coordinates : "",
        ...branchCoordinates,
        toNode ? toNode.coordinates : "",
      ];
      return coordinates.every((v) => v) ? coordinates : [];
    },
    projectUpdate($event, project) {
      console.debug($event, project);
    },
    outputValue(projectName) {
      if (!this.outputs || !this.outputs[this.heatmapYear]) return "";
      const project = this.outputs[this.heatmapYear].find((p) => p.name === projectName);
      if (!project || !project.results || !project.results[this.heatmapAsset]) return "";
      const assetValue = Object.values(project.results[this.heatmapAsset])[0];
      return assetValue
        ? `${this.measures.find((m) => m.value === this.heatmapMeasure).text}: ${this.formatValue(
            assetValue[this.heatmapMeasure]
          )}`
        : "";
    },
    formatValue(val) {
      let value = val;
      if (val > 1000000) {
        value = `${(val / 1000000).toFixed(0)}M`;
      } else if (val > 100000) {
        value = `${(val / 1000).toFixed(0)}k`;
      } else if (val < 100) {
        value = val.toFixed(2);
      } else if (val) {
        value = val.toFixed(0);
      }
      value = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return value;
    },
    getIcon(icon, color = null) {
      switch (icon) {
        case "storage":
          return divIcon({
            className: "my-custom-pin",
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${
                  color || "#7252A1"
                }" stroke="white" />
                <g transform="scale(0.45 0.45) translate(14 9)">
                <path d="M16.67,4H15V2H9V4H7.33A1.33,1.33 0 0,0 6,5.33V20.66C6,21.4 6.6,22 7.33,22H16.66C17.4,22 18,21.4 18,20.67V5.33C18,4.6 17.4,4 16.67,4M11,20V14.5H9L13,7V12.5H15" fill="white"/>
                </g>
              </svg>`,
            iconAnchor: [24, 44],
          });
        case "solarPv":
          return divIcon({
            className: "my-custom-pin",
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${
                  color || "#CEBA4D"
                }" stroke="white"/>
                <g transform="scale(0.45 0.45) translate(14.5 9)">
                <path d="M3.55,18.54L4.96,19.95L6.76,18.16L5.34,16.74M11,22.45C11.32,22.45 13,22.45 13,22.45V19.5H11M12,5.5A6,6 0 0,0 6,11.5A6,6 0 0,0 12,17.5A6,6 0 0,0 18,11.5C18,8.18 15.31,5.5 12,5.5M20,12.5H23V10.5H20M17.24,18.16L19.04,19.95L20.45,18.54L18.66,16.74M20.45,4.46L19.04,3.05L17.24,4.84L18.66,6.26M13,0.55H11V3.5H13M4,10.5H1V12.5H4M6.76,4.84L4.96,3.05L3.55,4.46L5.34,6.26L6.76,4.84Z" fill="white"/>
                </g>
              </svg>`,
            iconAnchor: [24, 44],
          });
        case "wind":
          return divIcon({
            className: "my-custom-pin",
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${
                  color || "#509E2F"
                }" stroke="white"/>
                <g transform="scale(0.45 0.45) translate(14.5 9)">
                <path d="M13.33,11.67L16.21,14.58C17.62,13.16 16.21,11.75 16.21,11.75L14.72,10.24C14.9,9.86 15,9.44 15,9C15,7.95 14.46,7.03 13.64,6.5L15,2.11C13.09,1.53 12.5,3.44 12.5,3.44L11.69,6.03C10.46,6.16 9.46,7 9.13,8.18L4.67,9.63C5.31,11.53 7.2,10.9 7.2,10.9L9.27,10.23C9.61,10.97 10.23,11.54 11,11.82V19C11,19 9,19 9,21C9,21.5 9,21.81 9,22H15V21C15,21 15,19 13,19V11.82C13.12,11.78 13.23,11.72 13.33,11.67M10.5,9A1.5,1.5 0 0,1 12,7.5A1.5,1.5 0 0,1 13.5,9A1.5,1.5 0 0,1 12,10.5A1.5,1.5 0 0,1 10.5,9Z" fill="white"/>
                </g>
              </svg>`,
            iconAnchor: [24, 44],
          });
        case "thermal":
          return divIcon({
            className: "my-custom-pin",
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${
                  color || "#F05A28"
                }" stroke="white"/>
                <g transform="scale(0.45 0.45) translate(14 9)">
                <path d="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" fill="white"/>
                </g>
              </svg>`,
            iconAnchor: [24, 44],
          });
        case "hydro":
          return divIcon({
            className: "my-custom-pin",
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${
                  color || "#0082BF"
                }" stroke="white"/>
                <g transform="scale(0.45 0.45) translate(14 9)">
                <path d="M12,20A6,6 0 0,1 6,14C6,10 12,3.25 12,3.25C12,3.25 18,10 18,14A6,6 0 0,1 12,20Z" fill="white"/>
                </g>
              </svg>`,
            iconAnchor: [24, 44],
          });

        default:
          return divIcon({
            className: "my-custom-pin",
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="48px" width="48px">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${
                  color || "#306d8a"
                }" stroke="${color ? "white" : "#90d7ec"}"/>
                <g transform="scale(0.7 0.7) translate(4.5 2.5)">
                <path d="M11.48 20v-6.26H8L13 4v6.26h3.35L11.48 20z" fill="white"/>
                </g>
              </svg>`,
            iconAnchor: [24, 44],
          });
      }
    },
    getOpportunityZones() {
      this.isBatchPending = false;
      this.findOpportunityZones({
        query: { ...this.opportunityZonesParams.query, $limit: 0 },
      }).then((res) => {
        const totalRecords = res.total;
        const batchCalls = [];
        for (let skip = 0; skip < totalRecords; skip += 50) {
          batchCalls.push(
            this.findOpportunityZones({
              query: {
                ...this.opportunityZonesParams.query,
                $limit: Math.min(50, totalRecords - skip),
                $skip: skip,
              },
            })
          );
        }
        Promise.all(batchCalls).then(() => {
          this.isBatchPending = false;
        });
      });
    },
    addMapLayer(layer) {
      this.$emit("add-map-layer", layer);
    },
    startRename(layer) {
      this.renamingLayerId = layer.id;
      this.renameValue = layer.name;
    },
    confirmRename(layer) {
      if (this.renamingLayerId !== layer.id) return;
      this.renamingLayerId = null;
      const name = this.renameValue.trim();
      if (name && name !== layer.name) {
        this.$emit("rename-map-layer", { id: layer.id, name });
      }
    },
    removeMapLayer(layerId) {
      this.$emit("remove-map-layer", layerId);
    },
    layerFeatures(layer) {
      const filter = this.mapLayerFilters[layer.id] || {};
      return layer.features.filter((feature) => {
        if (filter.category && feature.category !== filter.category) return false;
        if (filter.categorySecondary && feature.categorySecondary !== filter.categorySecondary) {
          return false;
        }
        return true;
      });
    },
    layerRadius(layer, value) {
      const MIN_RADIUS = 4;
      const MAX_RADIUS = 20;
      if (!layer.valueDomain || value == null) return MIN_RADIUS;
      const { min, max } = layer.valueDomain;
      if (max === min) return (MIN_RADIUS + MAX_RADIUS) / 2;
      const scaled = Math.sqrt((value - min) / (max - min));
      return MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * scaled;
    },
    fillColor(layer, feature) {
      if (feature.color) return feature.color;
      if (feature.category && layer.categories && layer.categories[feature.category]) {
        return layer.categories[feature.category];
      }
      return "#78909c";
    },
    strokeColor(layer, feature) {
      if (
        feature.categorySecondary &&
        layer.categoriesSecondary &&
        layer.categoriesSecondary[feature.categorySecondary]
      ) {
        return layer.categoriesSecondary[feature.categorySecondary];
      }
      return "#ffffff";
    },
  },
};
</script>

<style #scoped>
.clusterIcon {
  border: none;
}
.logo {
  max-height: 40px;
  opacity: 0.8;
}
.logo-control {
  padding-bottom: 0px;
  text-align: end;
  margin-bottom: 0px !important;
  margin-right: 4px !important;
}
.heatmap-control {
  margin-bottom: 4px !important;
}
.leaflet-control-layers-expanded {
  z-index: 2000;
}
.leaflet-bottom {
  z-index: 500;
}
.leaflet-control-layers {
  border: none !important;
}
</style>
